import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  current,
} from '@reduxjs/toolkit';
import client from '../clients/client';
import { State } from '../redux/redux.state';
import { TranslationResponse } from '../models/translation.response';
import { UpdateTranslationsRequest } from '../requests/update.translation.request';
import {
  AnswerType,
  TranslatedText,
  TranslationPill,
} from '../models/translated.text';

type QuestionTranslation = {
  questionLabel: string;
  translatedQuestion: TranslatedText;
  answers: AnswerType[];
  translationType: 'default' | 'widget' | 'category';
};

type TranslationState = State<TranslationResponse | undefined> & {
  updateStatus: 'idle' | 'loading' | 'successful' | 'error';
  updateError?: any;
  currentLanguage: string;
  currentTranslation: QuestionTranslation | undefined;
  supportedLanguages: string[];
  customTranslations: TranslatedText[];
  supportedCategories: TranslationPill[];
};

const initialState: TranslationState = {
  data: undefined,
  status: 'idle',
  updateStatus: 'idle',
  currentLanguage: 'english', // Get from the local storage
  currentTranslation: undefined,
  supportedLanguages: [],
  customTranslations: [],
  supportedCategories: [],
};

// Get translations
export const getTranslations = createAsyncThunk(
  'translations/get',
  async (shopId: string, { rejectWithValue }) => {
    try {
      const { data } = await client.get(`/translations/${shopId}`);
      return data;
    } catch (error: any) {
      const { status, data } = error.response;
      return rejectWithValue({ status, errorMessage: data });
    }
  }
);
export const updateTranslations = createAsyncThunk(
  'translations/update',
  async (
    { shopId, translations, translationType }: UpdateTranslationsRequest,
    { rejectWithValue }
  ) => {
    try {
      const { data } = await client.post(`/translations/update/${shopId}`, {
        translations,
        translation_type: translationType,
      });
      return data;
    } catch (error: any) {
      const { status, data } = error.response;
      return rejectWithValue({ status, errorMessage: data });
    }
  }
);

const translationSlice = createSlice({
  name: 'translations',
  initialState,
  reducers: {
    resetUpdateTranslationStatus: (state) => {
      state.updateStatus = 'idle';
    },
    setCurrentLanguage: (state, action: PayloadAction<string>) => {
      state.currentLanguage = action.payload;
    },
    setInitialDefaultTranslation: (state, action: PayloadAction<string>) => {
      if (!state.data) {
        return;
      }
      const key = action.payload;
      const {
        default_translations: defaultTranslations,
        custom_translations: customTranslations,
        flow,
      } = state.data.default_translations;

      const label = defaultTranslations.find((t) => t.key === key);

      const customTranslationFilter = (t: TranslatedText) =>
        t.key === key &&
        t.language === state.currentLanguage.toLowerCase() &&
        t.value !== label!.value;

      const question: TranslatedText =
        state.customTranslations.find(customTranslationFilter) ??
        customTranslations.find(customTranslationFilter) ??
        label!;

      const questionFlows = flow.find((f: any) => f.key === action.payload);
      const hasOptions =
        questionFlows && questionFlows.hasOwnProperty('options');
      const options = hasOptions ? questionFlows.options : [];
      let answers = [];
      for (let option of options) {
        const answerLabel = defaultTranslations.find(
          (t) => t.key === option.key
        )?.value;
        const value =
          state.customTranslations.find(
            (t) =>
              t.key === option.key &&
              t.language === state.currentLanguage.toLowerCase()
          )?.value ??
          customTranslations.find(
            (t) =>
              t.key === option.key &&
              t.language === state.currentLanguage.toLowerCase()
          )?.value ??
          answerLabel;
        answers.push({
          key: option.key,
          label: answerLabel!,
          value: value!,
        });
      }
      state.currentTranslation = {
        questionLabel: label!.value,
        translatedQuestion: {
          ...question,
          language: state.currentLanguage.toLowerCase(),
        },
        translationType: 'default',
        answers,
      };
    },
    setInitialCategoryTranslation: (state, action: PayloadAction<string>) => {
      if (!state.data) {
        return;
      }
      const key = action.payload;
      const {
        default_translations: defaultTranslations,
        custom_translations: customTranslations,
      } = state.data.category_translations;

      const label = defaultTranslations.find(
        (t) => t.key.toLocaleLowerCase() === key.toLocaleLowerCase()
      );

      const customTranslationFilter = (t: TranslatedText) =>
        t.key === key &&
        t.language === state.currentLanguage &&
        t.shop_id !== '*';

      const customTranslationFilterDefaultShopId = (t: TranslatedText) =>
        t.key === key && t.language === state.currentLanguage;

      const question: TranslatedText =
        state.customTranslations.find(customTranslationFilter) ??
        state.customTranslations.find(customTranslationFilterDefaultShopId) ??
        customTranslations.find(customTranslationFilter) ??
        customTranslations.find(customTranslationFilterDefaultShopId) ??
        label!;

      let answers: AnswerType[] = [];
      state.currentTranslation = {
        questionLabel: label?.value || '',
        translatedQuestion: {
          ...question,
          language: state.currentLanguage.toLowerCase(),
        },
        translationType: 'category',
        answers,
      };
    },
    setInitialWidgetTranslation: (state, action: PayloadAction<string>) => {
      if (!state.data) {
        return;
      }
      const key = action.payload;
      const {
        default_translations: defaultTranslations,
        custom_translations: customTranslations,
      } = state.data.widget_translations;

      const label = defaultTranslations.find((t) => t.key === key);

      const customTranslationFilter = (t: TranslatedText) =>
        t.key === key &&
        t.language === state.currentLanguage &&
        t.shop_id !== '*';

      const customTranslationFilterDefaultShopId = (t: TranslatedText) =>
        t.key === key && t.language === state.currentLanguage;

      const question: TranslatedText =
        state.customTranslations.find(customTranslationFilter) ??
        state.customTranslations.find(customTranslationFilterDefaultShopId) ??
        customTranslations.find(customTranslationFilter) ??
        customTranslations.find(customTranslationFilterDefaultShopId) ??
        label!;

      let answers: AnswerType[] = [];
      state.currentTranslation = {
        questionLabel: label!.value,
        translatedQuestion: {
          ...question,
          language: state.currentLanguage.toLowerCase(),
        },
        translationType: 'widget',
        answers,
      };
    },
    updateTranslatedQuestion: (state, action: PayloadAction<string>) => {
      if (state.currentTranslation) {
        let currentTranslation = state.currentTranslation?.translatedQuestion;
        state.currentTranslation.translatedQuestion = {
          ...currentTranslation,
          value: action.payload,
        };
        const otherTranslations = state.customTranslations.filter(
          (t) =>
            (t.language.toLowerCase() !== state.currentLanguage.toLowerCase() &&
              t.key === state.currentTranslation?.translatedQuestion.key) ||
            (t.language.toLowerCase() === state.currentLanguage.toLowerCase() &&
              t.key !== state.currentTranslation?.translatedQuestion.key)
        );
        state.customTranslations = [
          ...otherTranslations,
          state.currentTranslation.translatedQuestion,
        ];
      }
    },
    updateAnswerTranslation: (
      state,
      action: PayloadAction<{ key: string; value: string }>
    ) => {
      const { key, value } = action.payload;
      if (state.currentTranslation) {
        const answers = state.currentTranslation.answers;
        if (answers.length > 0) {
          let changedAnswer = answers.find((ans) => ans.key === key);
          let changedAnswerIndex = answers.findIndex((ans) => ans.key === key);
          let otherAnswers = answers.filter((ans) => ans.key !== key);

          if (!changedAnswer) {
            return;
          }
          changedAnswer.value = value;
          otherAnswers.splice(changedAnswerIndex, 0, changedAnswer);
          state.currentTranslation.answers = otherAnswers;

          const otherTranslations = state.customTranslations.filter(
            (t) => t.key !== key
          );
          otherTranslations.push({
            key,
            value,
            language: state.currentLanguage.toLowerCase(),
          });
          state.customTranslations = otherTranslations;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTranslations.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(
        getTranslations.fulfilled,
        (state, action: PayloadAction<TranslationResponse>) => {
          state.data = action.payload;

          const customTranslations =
            state.data!.default_translations.custom_translations;

          const distinctLanguage = new Set<string>();
          // Add english
          distinctLanguage.add('english');

          for (let customTranslation of customTranslations) {
            let language = customTranslation.language
              .toLowerCase()
              .includes('spanish')
              ? 'spanish'
              : customTranslation.language;
            distinctLanguage.add(language);
          }

          state.supportedLanguages = Array.from(distinctLanguage.values());
          state.supportedCategories =
            state.data.category_translations.default_translations.map((e) => ({
              name: e.key,
              key: e.key,
            }));
          state.data.category_translations.custom_translations.forEach((e) => {
            if (e.language.toLowerCase() === 'english') {
              state.supportedCategories.push({ name: e.value, key: e.key });
            }
          });
          state.status = 'successful';
        }
      )
      .addCase(getTranslations.rejected, (state, action) => {
        state.status = 'error';
        state.errors = action.payload;
      })
      .addCase(updateTranslations.pending, (state) => {
        state.updateStatus = 'loading';
      })
      .addCase(updateTranslations.fulfilled, (state) => {
        state.updateStatus = 'successful';
      })
      .addCase(updateTranslations.rejected, (state, action) => {
        state.updateStatus = 'error';
        state.updateError = action.payload;
      });
  },
});

export const {
  setCurrentLanguage,
  setInitialDefaultTranslation,
  setInitialCategoryTranslation,
  setInitialWidgetTranslation,
  updateTranslatedQuestion,
  updateAnswerTranslation,
  resetUpdateTranslationStatus,
} = translationSlice.actions;

export default translationSlice.reducer;
